import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { AnimatePresence, motion, useMotionValue, useTransform, animate } from 'framer-motion';
import TabRail, { TabRailMobile } from './TabRail';
import { SECTIONS } from './sections';
import { useOnboarding } from '../onboarding/OnboardingContext';

import CollegeFinder from '../sections/CollegeFinder';
import ExamPrep from '../sections/ExamPrep';
import Quizzes from '../sections/Quizzes';
import Journal from '../sections/Journal';
import Contact from '../sections/Contact';

const SECTION_MAP = {
    home: CollegeFinder,
    'exam-prep': ExamPrep,
    quizzes: Quizzes,
    journal: Journal,
    contact: Contact,
};

const DUR = 1.15;

export default function FolioBook() {
    const [active, setActive] = useState('home');
    const [pendingId, setPendingId] = useState(null);   // incoming — rendered ONLY on the back face
    const [direction, setDirection] = useState('forward');
    const [flipping, setFlipping] = useState(false);

    const activeMeta = SECTIONS.find((s) => s.id === active);
    const pendingMeta = pendingId ? SECTIONS.find((s) => s.id === pendingId) : null;
    const ActiveComp = SECTION_MAP[active];

    // Motion value: 0 → -180 (forward) or 0 → +180 (reverse)
    const rotate = useMotionValue(0);

    // Shading opacity peaks mid-flip (edge-on view)
    const flipShade = useTransform(rotate, (v) => Math.sin((Math.abs(v) / 180) * Math.PI) * 0.55);
    // Gutter shadow strongest at start, fades to 0 at end
    const gutterShade = useTransform(rotate, (v) => 0.55 * (1 - Math.abs(v) / 180));
    // Front face visible while |v| < 90; hidden past that (matches backface-visibility)
    const frontOpacity = useTransform(rotate, (v) => (Math.abs(v) < 90 ? 1 : 0));
    // Back face visible only past 90 (matches when its rotated 180° face becomes forward-facing)
    const backOpacity = useTransform(rotate, (v) => (Math.abs(v) >= 90 ? 1 : 0));

    const goTo = useCallback(
        (id) => {
            if (id === active || flipping) return;
            const curIdx = SECTIONS.findIndex((s) => s.id === active);
            const newIdx = SECTIONS.findIndex((s) => s.id === id);
            const dir = newIdx > curIdx ? 'forward' : 'reverse';

            // KEY FIX: do NOT touch `active` yet.
            // Base pages continue to show the CURRENT chapter through the entire arc.
            // The overlay's back face renders the incoming chapter (kept hidden by
            // backface-visibility until rotate crosses ±90°).
            setDirection(dir);
            setPendingId(id);
            setFlipping(true);
            rotate.set(0);

            const target = dir === 'forward' ? -180 : 180;
            animate(rotate, target, {
                duration: DUR,
                ease: [0.72, 0, 0.28, 1],
                onComplete: () => {
                    // Atomic handoff at the exact moment the page lies flat again:
                    // React batches these — one paint, no flash.
                    setActive(id);
                    setFlipping(false);
                    setPendingId(null);
                    rotate.set(0);
                },
            });
        },
        [active, flipping, rotate],
    );

    // Always-current ref so window-event listeners (registered once) never
    // call a stale goTo closure that carries an outdated `active` section.
    const goToRef = useRef(goTo);
    useEffect(() => { goToRef.current = goTo; }, [goTo]);

    // Smart scrolling: when the pointer is inside the book, capture the wheel
    // AND touchmove events and forward them to the right page's inner scroll
    // container. Strict isolation — body scroll is frozen while the pointer is
    // inside the book bounds so nothing bleeds through.
    const bookRef = useRef(null);
    const innerRef = useRef(null);
    const hoverRef = useRef(false);

    useEffect(() => {
        const el = bookRef.current;
        if (!el) return;

        const onWheel = (e) => {
            const inner = innerRef.current;
            if (!inner) return;
            // Right-side tab rail is OUTSIDE the book boundary — scrolling there
            // must not scroll the book's pages (the global handler locks page scroll).
            if (e.target.closest('[data-testid="folio-tab-rail"]')) return;
            e.preventDefault();
            e.stopPropagation();
            inner.scrollTop += e.deltaY;
        };
        const onTouchMove = (e) => { e.stopPropagation(); };
        const onEnter = () => { document.body.style.overflow = 'hidden'; };
        const onLeave = () => { document.body.style.overflow = ''; };

        el.addEventListener('wheel', onWheel, { passive: false });
        el.addEventListener('touchmove', onTouchMove, { passive: true });
        el.addEventListener('mouseenter', onEnter);
        el.addEventListener('mouseleave', onLeave);

        // Right-side outside-book scroll lock:
        // when the pointer is to the RIGHT of the book bounds, absorb wheel
        // events so the page can't scroll. Left of the book, allow native page scroll.
        const onGlobalWheel = (e) => {
            const rect = el.getBoundingClientRect();
            const insideBookX = e.clientX >= rect.left && e.clientX <= rect.right;
            const insideBookY = e.clientY >= rect.top && e.clientY <= rect.bottom;
            if (insideBookX && insideBookY) return; // handled by onWheel above
            if (e.clientX > rect.right) {
                // Cursor to the right of the book → disable page scroll here.
                e.preventDefault();
            }
            // Cursor to the left (or above/below with left-of alignment) → default page scroll.
        };
        window.addEventListener('wheel', onGlobalWheel, { passive: false });

        // Search deep-link listener
        const onGoTo = (ev) => {
            const id = ev?.detail?.id;
            if (!id) return;
            goToRef.current(id);
        };
        window.addEventListener('folio:goTo', onGoTo);

        return () => {
            el.removeEventListener('wheel', onWheel);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('mouseenter', onEnter);
            el.removeEventListener('mouseleave', onLeave);
            window.removeEventListener('wheel', onGlobalWheel);
            window.removeEventListener('folio:goTo', onGoTo);
            document.body.style.overflow = '';
        };
        // eslint-disable-next-line
    }, []);

    return (
        <div className="w-full">
            <TabRailMobile activeId={active} onSelect={goTo} />

            {/* Desktop folio book */}
            <div className="hidden lg:flex justify-center py-10 px-6 xl:px-14">
                <div
                    ref={bookRef}
                    data-testid="folio-book"
                    className="folio-stage relative"
                    style={{ width: 'min(1240px, 95vw)', aspectRatio: '1.55 / 1' }}
                >
                    {/* Book base — SINGLE unified spread */}
                    <div
                        className="absolute inset-0 folio-shadow rounded-sm flex"
                        style={{ perspective: 2600, transformStyle: 'preserve-3d' }}
                    >
                        {/* LEFT PAGE */}
                        <div className="relative w-1/2 h-full paper deckle-r overflow-hidden grain-light rounded-l-sm">
                            <LeftPage meta={activeMeta} />
                            {/* left inner gutter shadow */}
                            <div className="pointer-events-none absolute inset-y-0 right-0 w-24 z-[2]"
                                 style={{ background: 'linear-gradient(to left, rgba(59,68,30,0.35) 0%, rgba(59,68,30,0) 100%)' }} />
                        </div>

                        {/* RIGHT PAGE */}
                        <div className="relative w-1/2 h-full paper deckle-l overflow-hidden grain-light rounded-r-sm">
                            <div ref={innerRef} className="absolute inset-0 overflow-auto no-scrollbar book-scroll">
                                <div className="min-h-full p-10 xl:p-14">
                                    <ActiveComp goTo={goTo} />
                                </div>
                            </div>
                            <div className="pointer-events-none absolute inset-y-0 left-0 w-16 z-[2]"
                                 style={{ background: 'linear-gradient(to right, rgba(59,68,30,0.30) 0%, rgba(59,68,30,0) 100%)' }} />
                            <motion.div
                                className="pointer-events-none absolute inset-y-0 left-0 w-32 z-[3]"
                                style={{
                                    opacity: gutterShade,
                                    background:
                                        'linear-gradient(to right, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 100%)',
                                }}
                            />
                        </div>

                        {/* CENTER SEAM overlay (absolute, no gap) */}
                        <div className="pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 w-[3px] z-[8]"
                             style={{
                                 background:
                                    'linear-gradient(90deg, rgba(59,68,30,0.35) 0%, rgba(0,0,0,0.7) 50%, rgba(59,68,30,0.35) 100%)',
                             }} />
                        {/* deep inner gutter shadow across the seam */}
                        <div className="pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 w-24 z-[7]"
                             style={{
                                 background:
                                    'radial-gradient(ellipse 40% 100% at 50% 50%, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 70%)',
                             }} />

                        {/* === RING BINDER === (4 metallic rings) */}
                        <RingBinder />

                        {/* === FLIPPING PAGE === */}
                        <AnimatePresence>
                            {flipping && pendingMeta && (
                                <FlipLayer
                                    key={`flip-${active}-${pendingId}-${direction}`}
                                    direction={direction}
                                    rotate={rotate}
                                    flipShade={flipShade}
                                    frontOpacity={frontOpacity}
                                    backOpacity={backOpacity}
                                    frontMeta={activeMeta}
                                    backMeta={pendingMeta}
                                />
                            )}
                        </AnimatePresence>
                    </div>

                    <TabRail activeId={active} onSelect={goTo} />

                    <div className="absolute -top-6 left-2 mono text-[10px] text-[color:var(--parchment)]/70">
                        Folio No. 2026 — Vol. I
                    </div>
                    <div className="absolute -bottom-6 right-2 mono text-[10px] text-[color:var(--parchment)]/70">
                        {activeMeta.chapter} / IV
                    </div>
                </div>
            </div>

            {/* Mobile */}
            <div className="lg:hidden">
                <MobileMeta meta={activeMeta} />
                <div className="paper grain-light relative">
                    <div className="p-5 sm:p-8">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={active}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.5, ease: [0.83, 0, 0.17, 1] }}
                            >
                                <ActiveComp goTo={goTo} />
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ============================================================
// FLIP LAYER — bi-directional 3D page turn
// ============================================================
function FlipLayer({ direction, rotate, flipShade, frontOpacity, backOpacity, frontMeta, backMeta }) {
    const isForward = direction === 'forward';

    // Forward: overlay on RIGHT half, transform-origin: left (spine)
    // Reverse: overlay on LEFT half, transform-origin: right (spine)
    const layerStyle = isForward
        ? { top: 0, right: 0, width: 'calc(50% - 1.5px)', height: '100%' }
        : { top: 0, left: 0, width: 'calc(50% - 1.5px)', height: '100%' };

    const origin = isForward ? 'left center' : 'right center';

    return (
        <motion.div
            className="absolute pointer-events-none"
            style={{
                ...layerStyle,
                transformStyle: 'preserve-3d',
                zIndex: 25,
            }}
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.14 }}
        >
            <motion.div
                className="absolute inset-0"
                style={{
                    transformStyle: 'preserve-3d',
                    transformOrigin: origin,
                    rotateY: rotate,
                    willChange: 'transform',
                }}
            >
                {/* FRONT FACE — outgoing page */}
                <motion.div
                    className={`absolute inset-0 paper grain-light overflow-hidden ${
                        isForward ? 'rounded-r-sm' : 'rounded-l-sm'
                    }`}
                    style={{
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden',
                        opacity: frontOpacity,
                        boxShadow: isForward
                            ? 'inset 14px 0 32px -8px rgba(59,68,30,0.4), 0 20px 40px -10px rgba(0,0,0,0.4)'
                            : 'inset -14px 0 32px -8px rgba(59,68,30,0.4), 0 20px 40px -10px rgba(0,0,0,0.4)',
                    }}
                >
                    <PageFace meta={frontMeta} kind={isForward ? 'was-content' : 'was-meta'} />
                    <motion.div
                        className="pointer-events-none absolute inset-0"
                        style={{
                            opacity: flipShade,
                            background: isForward
                                ? 'linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 40%, rgba(255,255,255,0.18) 100%)'
                                : 'linear-gradient(-90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 40%, rgba(255,255,255,0.18) 100%)',
                        }}
                    />
                </motion.div>

                {/* BACK FACE — new/arriving side */}
                <motion.div
                    className={`absolute inset-0 paper grain-light overflow-hidden ${
                        isForward ? 'rounded-l-sm' : 'rounded-r-sm'
                    }`}
                    style={{
                        transform: 'rotateY(180deg)',
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden',
                        opacity: backOpacity,
                        boxShadow: isForward
                            ? 'inset -14px 0 32px -8px rgba(59,68,30,0.4), 0 20px 40px -10px rgba(0,0,0,0.4)'
                            : 'inset 14px 0 32px -8px rgba(59,68,30,0.4), 0 20px 40px -10px rgba(0,0,0,0.4)',
                    }}
                >
                    <PageFace meta={backMeta} kind={isForward ? 'now-meta' : 'now-content'} />
                    <motion.div
                        className="pointer-events-none absolute inset-0"
                        style={{
                            opacity: flipShade,
                            background: isForward
                                ? 'linear-gradient(-90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 40%, rgba(255,255,255,0.15) 100%)'
                                : 'linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 40%, rgba(255,255,255,0.15) 100%)',
                        }}
                    />
                </motion.div>
            </motion.div>
        </motion.div>
    );
}

// ============================================================
// RING BINDER — 4 metallic chrome rings on the seam
// ============================================================
function RingBinder() {
    const positions = ['9%', '34%', '59%', '84%'];
    return (
        <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-0 h-full z-[35]"
             style={{ width: '48px' }}
             data-testid="ring-binder">
            {positions.map((top, i) => (
                <div key={i}
                     className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2"
                     style={{ top }}>
                    <Ring />
                </div>
            ))}
        </div>
    );
}

function Ring() {
    return (
        <div
            className="relative"
            style={{
                width: 42,
                height: 42,
                borderRadius: '50%',
                background:
                    'conic-gradient(from 210deg, #9d9d9d 0deg, #f4f4f4 60deg, #ffffff 90deg, #cfcfcf 150deg, #5a5a5a 210deg, #2a2a2a 260deg, #6f6f6f 300deg, #d1d1d1 360deg)',
                boxShadow:
                    '0 4px 8px rgba(0,0,0,0.55), 0 1px 0 rgba(255,255,255,0.4) inset, 0 -1px 0 rgba(0,0,0,0.4) inset',
            }}
        >
            {/* inner cutout — the "hole" of the ring */}
            <div
                className="absolute rounded-full"
                style={{
                    inset: 10,
                    background:
                        'radial-gradient(circle at 40% 30%, #efe6cf 0%, #d3c9a8 65%, #8a7f5c 100%)',
                    boxShadow:
                        'inset 0 3px 4px rgba(0,0,0,0.55), inset 0 -1px 0 rgba(255,255,255,0.35)',
                }}
            />
            {/* highlight sparkle */}
            <div
                className="absolute rounded-full"
                style={{
                    top: 3,
                    left: 6,
                    width: 14,
                    height: 6,
                    background:
                        'linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 100%)',
                    filter: 'blur(1.2px)',
                    borderRadius: '50%',
                }}
            />
        </div>
    );
}

// ============================================================
// PAGE FACES for flip overlay
// ============================================================
function PageFace({ meta, kind }) {
    // Renders a minimal, elegant chapter card on either face of a flipping page.
    // kind: was-content | was-meta | now-content | now-meta
    const isMeta = kind === 'was-meta' || kind === 'now-meta';
    const label = kind.startsWith('was') ? 'turning from' : 'arriving at';

    if (isMeta) {
        return (
            <div className="relative h-full flex flex-col justify-between p-10 xl:p-14">
                <div className="mono text-[10px] text-[color:var(--moss-deep)] text-right">
                    {label}
                </div>
                <div className="flex items-end justify-center">
                    <div
                        className="display-italic text-[color:var(--moss-deep)]"
                        style={{ fontSize: 'clamp(120px, 18vw, 240px)', lineHeight: 0.85 }}
                    >
                        {meta.chapter}
                    </div>
                </div>
                <div className="flex items-end justify-between">
                    <div className="mono text-[10px] text-[color:var(--moss-deep)]">
                        {meta.kicker}
                    </div>
                    <div className="h-1 w-24" style={{ background: meta.color }} />
                </div>
            </div>
        );
    }

    // content-face
    return (
        <div className="relative h-full flex flex-col justify-between p-10 xl:p-14">
            <div className="flex items-start justify-between">
                <div className="mono text-[10px] text-[color:var(--moss-deep)]">
                    §{meta.chapter} — {meta.label}
                </div>
                <div className="mono text-[10px] text-[color:var(--moss-deep)]">
                    {label}
                </div>
            </div>
            <div className="space-y-4 max-w-md">
                <div className="mono text-[10px] text-[color:var(--moss-deep)]">— {meta.kicker}</div>
                <h3
                    className="display text-[color:var(--ink)]"
                    style={{ fontSize: 'clamp(36px, 4vw, 52px)' }}
                >
                    {meta.subtitle}
                </h3>
            </div>
            <div className="flex items-end justify-between">
                <div className="h-1 w-24" style={{ background: meta.color }} />
                <div className="mono text-[10px] text-[color:var(--moss-deep)]">§{meta.chapter}</div>
            </div>
        </div>
    );
}

// ============================================================
// LEFT PAGE (base) & MOBILE META
// ============================================================
function LeftPage({ meta }) {
    const { user } = useOnboarding();
    const firstName = user?.name?.split(' ')[0];
    const isHome = meta.id === 'home';
    const subtitle = isHome && firstName ? `Welcome, ${firstName}.` : meta.subtitle;
    const kickerLine = isHome && user?.education
        ? `Chapter One · ${user.education}`
        : meta.kicker;

    return (
        <div className="relative h-full flex flex-col justify-between p-10 xl:p-14">
            <div className="flex items-start justify-between">
                <div className="mono text-[10px] text-[color:var(--moss-deep)]">
                    {kickerLine}
                </div>
                <div className="mono text-[10px] text-[color:var(--moss-deep)] text-right">
                    Studyfolio<br />Est. 2026
                </div>
            </div>

            <div className="flex flex-col gap-6">
                <div
                    className="display-italic text-[color:var(--moss-deep)]"
                    style={{ fontSize: 'clamp(120px, 18vw, 240px)', lineHeight: 0.85 }}
                >
                    {meta.chapter}
                </div>
                <div className="max-w-sm">
                    <div className="mono text-[11px] text-[color:var(--moss-deep)] mb-3">
                        {isHome && firstName ? '—— A folio for' : '—— On the matter of'}
                    </div>
                    <h2
                        className="display text-[color:var(--ink)]"
                        style={{ fontSize: 'clamp(38px, 4.5vw, 60px)' }}
                    >
                        {subtitle}
                    </h2>
                    {isHome && user?.goal && (
                        <div className="mt-4 display-italic text-[color:var(--ink-soft)] text-[15px] leading-snug">
                            “{user.goal}”
                        </div>
                    )}
                </div>
            </div>

            <div className="flex items-end justify-between">
                <div className="h-1 w-24" style={{ background: meta.color }} />
                <div className="mono text-[10px] text-[color:var(--moss-deep)]">
                    §{meta.chapter}.01
                </div>
            </div>
        </div>
    );
}

function MobileMeta({ meta }) {
    return (
        <div className="relative paper grain-light border-b border-[color:var(--moss-deep)]/20">
            <div className="p-5 sm:p-8 flex items-end justify-between">
                <div>
                    <div className="mono text-[10px] text-[color:var(--moss-deep)] mb-2">
                        {meta.kicker}
                    </div>
                    <h2 className="display text-[color:var(--ink)] text-3xl sm:text-4xl max-w-[80vw]">
                        {meta.subtitle}
                    </h2>
                </div>
                <div
                    className="display-italic text-[color:var(--moss-deep)] leading-none"
                    style={{ fontSize: 'clamp(60px, 20vw, 120px)' }}
                >
                    {meta.chapter}
                </div>
            </div>
            <div className="h-1" style={{ background: meta.color }} />
        </div>
    );
}
